import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { FiAlertTriangle, FiCheckCircle, FiClock, FiMail } from "react-icons/fi";
import { Spinner } from "components/ui";
import {
  UnsubscribePreview,
  confirmUnsubscribe,
  previewUnsubscribe,
} from "./unsubscribeConsentApi";

/**
 * One-click unsubscribe landing page.
 *
 * IDENTIFY THEN CONFIRM. The token in the link identifies the account and nothing
 * else — it grants no session and no account access, and cannot opt anyone back
 * in. The user is shown a masked address so they can tell whose preferences they
 * are about to change, then chooses what to withdraw.
 *
 * Works with no login, which §7 requires. The previous version unsubscribed
 * everything on a single click with no confirmation and no choice of scope.
 *
 * The address is masked rather than shown in full: the link may sit in a
 * forwarded email or a shared inbox, and the page should not disclose a full
 * address to whoever opens it.
 */
const UnsubscribeDM = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [preview, setPreview] = useState<UnsubscribePreview | null>(null);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [status, setStatus] = useState<"loading" | "ready" | "invalid" | "done" | "error">("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }

    previewUnsubscribe(token)
      .then((response: any) => {
        if (!response?.success || !response?.result) {
          setStatus("invalid");
          return;
        }
        const data = response.result as UnsubscribePreview;
        setPreview(data);
        // Everything the link covers is pre-selected for withdrawal: the user
        // clicked "unsubscribe", so opting OUT is the expected default here.
        // Pre-selecting an opt-IN would be the thing that is never acceptable.
        setSelected(Object.fromEntries(data.purposes.map((p) => [p.purpose, true])));
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  }, [token]);

  const mutation = useMutation(
    (purposes: string[]) => confirmUnsubscribe(token, purposes),
    {
      onSuccess: (response: any) => {
        setMessage(response?.message ?? "");
        setStatus(response?.success ? "done" : "error");
      },
      onError: () => setStatus("error"),
    }
  );

  const chosen = Object.entries(selected)
    .filter(([, v]) => v)
    .map(([k]) => k);

  return (
    <div className="flex w-full items-center justify-center min-h-[calc(100vh-var(--zs-public-header-h,64px))] px-4 py-10 sm:py-16">
      <div className="w-full max-w-lg p-6 zs-card sm:p-8">
        {status === "loading" && (
          <div className="flex flex-col items-center gap-3 py-6 text-sm text-center text-ink-3">
            <Spinner className="w-6 h-6" />
            <p>Checking your link…</p>
          </div>
        )}

        {status === "invalid" && (
          <div className="flex flex-col items-center text-center">
            <span className="zs-empty-icon bg-warning-soft text-warning">
              <FiClock />
            </span>
            <h2 className="mb-2 text-xl font-semibold font-display text-ink">This link has expired</h2>
            <p className="text-sm text-ink-3">
              Unsubscribe links stop working after a while, for your security. You can change your
              email preferences at any time by signing in and opening your account settings.
            </p>
          </div>
        )}

        {status === "ready" && preview && (
          <>
            <div className="mb-5">
              <span className="zs-eyebrow">
                <FiMail /> Email settings
              </span>
              <h2 className="text-xl font-semibold sm:text-2xl font-display text-ink">Manage your emails</h2>
              <p className="mt-1.5 text-sm text-ink-3">
                For the account ending <span className="font-medium text-ink">{preview.maskedEmail}</span>.
                Choose what you would like to stop receiving.
              </p>
            </div>

            <div className="mb-5 overflow-hidden border divide-y rounded-lg border-line divide-line">
              {preview.purposes.map((purpose) => (
                <div
                  key={purpose.purpose}
                  className="flex items-start gap-3 px-4 py-3.5 transition-colors hover:bg-surface-hover/60"
                >
                  <input
                    id={`unsub-${purpose.purpose}`}
                    type="checkbox"
                    checked={selected[purpose.purpose] ?? false}
                    onChange={(e) =>
                      setSelected((prev) => ({ ...prev, [purpose.purpose]: e.target.checked }))
                    }
                    className="mt-0.5 h-4 w-4 shrink-0 accent-danger cursor-pointer"
                  />
                  <label htmlFor={`unsub-${purpose.purpose}`} className="flex-1 mb-0 cursor-pointer">
                    <span className="block text-sm font-medium text-ink">{purpose.label}</span>
                    <span className="block mt-0.5 text-xs leading-snug text-ink-3">
                      {purpose.description}
                    </span>
                    {!purpose.granted ? (
                      <span className="mt-1.5 block text-[11px] text-ink-4">
                        You are not currently receiving these.
                      </span>
                    ) : null}
                  </label>
                </div>
              ))}
            </div>

            <button
              type="button"
              disabled={chosen.length === 0 || mutation.isLoading}
              onClick={() => mutation.mutate(chosen)}
              className="zs-btn zs-btn-danger zs-btn-lg zs-btn-block"
            >
              {mutation.isLoading ? "Updating…" : "Confirm unsubscribe"}
            </button>

            <p className="mt-4 text-xs leading-snug text-ink-4">
              You will still receive essential emails about your account, such as password resets and
              security alerts. These are not marketing and are not affected by this choice.
            </p>
          </>
        )}

        {status === "done" && (
          <div className="flex flex-col items-center text-center">
            <span className="zs-empty-icon bg-success-soft text-success">
              <FiCheckCircle />
            </span>
            <h2 className="mb-2 text-xl font-semibold font-display text-success">Done</h2>
            <p className="text-sm text-ink-2">{message}</p>
            <p className="mt-4 text-xs text-ink-4">
              Changed your mind? Sign in and open your account settings to turn any of these back on.
            </p>
          </div>
        )}

        {status === "error" && (
          <div className="flex flex-col items-center text-center">
            <span className="zs-empty-icon bg-danger-soft text-danger">
              <FiAlertTriangle />
            </span>
            <h2 className="mb-2 text-xl font-semibold font-display text-danger">Something went wrong</h2>
            <p className="text-sm text-ink-3">
              {message || "Please try again in a moment, or contact support if it keeps happening."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnsubscribeDM;
